import React from 'react';
import styled from 'styled-components';

import Alert from 'components/Icons/Alert';
import Info from 'components/Icons/Info';
import colors from 'utils/colors';
import device from 'utils/device';

type TextProps = React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>;

export type BaseProps = React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLDivElement>,
  HTMLDivElement
> & {
  /**
   * Optional wrapper class name attached
   */
  className?: string;
  /**
   * Type of the notification box
   */
  type: 'Error' | 'Info';
  /**
   * Props passed to Text component
   */
  textProps?: TextProps;
  /**
   * optional children to override the content
   */
  children?: React.ReactNode;
};

const Wrapper = styled.div<BaseProps>`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  justify-content: flex-start;
  min-width: auto;
  max-width: 500px;
  margin-top: 24px;

  @media ${device.isDesktop}, ${device.isTablet} {
    min-width: 335px;
  }

  ${props =>
    props.type === 'Error'
      ? `padding: 10px;`
      : `
    background-color: ${colors.blueGrey75};
  `};
  border: ${props => (props.type === 'Error' ? `solid 3px ${colors.red}` : `none`)};
`;

const IconWrapper = styled.div<BaseProps>`
  align-self: flex-start;
  padding-top: ${props => (props.type === 'Error' ? '10px' : '15px')};
  padding-left: 10px;
  padding-right: 10px;
  min-width: ${props => (props.type === 'Error' ? '29px' : '25px')};
  max-width: ${props => (props.type === 'Error' ? '29px' : '25px')};
  background-color: ${props => (props.type === 'Error' ? colors.white : colors.blueGrey75)};
`;

const TextWrapper = styled.div<BaseProps>`
  background-color: ${props => (props.type === 'Error' ? colors.white : colors.blueGrey10)};
  color: ${props => (props.type === 'Error' ? colors.red : colors.darkGrey)};
  flex-basis: 100%;
  padding: ${props => (props.type === 'Error' ? '10px 15px 0px' : '15px')};
  ${props => props.type === 'Info' && `padding-left: 15px;`};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  overflow: hidden;
`;

const Base: React.FC<BaseProps> = ({ type, children, textProps, ...props }) => {
  return (
    <Wrapper type={type} {...props}>
      <IconWrapper type={type}>
        {type === 'Error' && <Alert fill={colors.red} />}
        {type === 'Info' && <Info fill={colors.white} />}
      </IconWrapper>
      <TextWrapper type={type} {...textProps}>
        {children}
      </TextWrapper>
    </Wrapper>
  );
};

export default Base;
